/**
 * Shared constants for the FloatingWindow primitive.
 *
 * The window layer deliberately lives in a bounded z-index band UNDER 200 so
 * that in-body Radix `dropdown-menu` / `select` (portaled to document.body at
 * z-[200]) still render above a focused window. In-body `popover` / `tooltip`
 * (z-[50]) are handled by portaling into the per-window container instead — see
 * FloatingWindowContainerContext in FloatingWindowFrame.tsx.
 */

/** Base z-index for the least-recently-focused window. */
export const Z_BASE = 150;
/** Upper bound for the most-recently-focused window (must stay < 200). */
export const Z_MAX = 190;

/** Minimum window size in px. */
export const MIN_SIZE = { w: 320, h: 200 } as const;
/** Default window size when neither an explicit nor a persisted size exists. */
export const DEFAULT_SIZE = { w: 480, h: 560 } as const;

/**
 * Top-left anchor for the first window of a given type. Clears the ~100px left
 * nav and the top bar so a freshly-opened window never spawns under app chrome.
 */
export const BASE_POSITION = { x: 120, y: 96 } as const;

/** Down-right nudge applied per already-open instance of the same type. */
export const CASCADE_OFFSET = 28;
/** Number of cascade steps before wrapping back toward BASE_POSITION. */
export const CASCADE_WRAP = 8;

/** localStorage key prefix for per-type persisted size. */
export const SIZE_STORAGE_PREFIX = 'floating-window-size:';

/**
 * Inset around a maximized window so its chrome doesn't touch the viewport
 * edges. A maximized window is edge-anchored via CSS (top/left/right/bottom =
 * this value) rather than a fixed pixel size, so it tracks the viewport in both
 * directions on browser resize.
 */
export const MAXIMIZE_MARGIN = 16;
